// The withdrawal controller (bead hf-e6-commitments-coverage-udt.2).
//
// The mirror of `useClaim`, and deliberately the same shape: idle → submitting →
// settled, no Convex, handed a function that performs the withdrawal and returns the
// contract's `WithdrawCommitmentResult`.
//
// ── WHY IT IS A SEPARATE CONTROLLER ─────────────────────────────────────────────
//
// Withdrawing is not claiming-in-reverse. It carries an optional reason code, it
// cannot lose a race, and its refusals mean different things: `not_responsible` is
// somebody else's commitment (COM-010), where a claim never sees that at all. Sharing
// one controller would mean a union of two state machines with half the fields unused
// in each direction, and the first person to add a case would have to work out which
// half they were in.
//
// ── AND WHY IT STILL REFRESHES THE PLAN ─────────────────────────────────────────
//
// Same reason `useClaim` does: after a successful withdrawal the screen is wrong. It
// says somebody is helping, and nobody is. Under a live Convex query the surrounding
// view updates on its own; the callback exists so the fixture-backed surfaces can be
// tested for it rather than assumed to have it.

import type { CommitmentRejection, commitments as contracts } from "@herefor/contracts";
import { useCallback, useRef, useState } from "react";

export type WithdrawResultLike = contracts.WithdrawCommitmentResult;
export type WithdrawalReasonCode = contracts.WithdrawalReasonCode;

export type WithdrawState =
  | { readonly status: "idle" }
  /** In flight. The control is disabled from here, so a double tap cannot be sent. */
  | { readonly status: "submitting" }
  | { readonly status: "withdrawn" }
  | { readonly status: "refused"; readonly rejection: CommitmentRejection };

export interface UseWithdrawOptions {
  /** Performs the withdrawal. In production this calls the boundary-wrapped mutation. */
  readonly withdraw: (reasonCode?: WithdrawalReasonCode) => Promise<WithdrawResultLike>;
  /** Called once the plan has demonstrably moved — a successful withdrawal only. */
  readonly onPlanChanged?: () => void;
}

export interface WithdrawController {
  readonly state: WithdrawState;
  /**
   * Step back.
   *
   * The reason is OPTIONAL at the type level, not merely at the UI level: §10.10
   * forbids demanding an excuse, and a required parameter would make the product's
   * position on that a matter of remembering to pass `undefined`.
   */
  readonly submit: (reasonCode?: WithdrawalReasonCode) => Promise<void>;
  readonly reset: () => void;
}

export function useWithdraw({ withdraw, onPlanChanged }: UseWithdrawOptions): WithdrawController {
  const [state, setState] = useState<WithdrawState>({ status: "idle" });
  /**
   * In-flight guard in a ref, for the reason `useClaim` gives: a disabled button is
   * not enough, because a second tap can be dispatched before React re-renders and
   * state read in that window is still the old state.
   */
  const inFlight = useRef(false);

  const submit = useCallback(
    async (reasonCode?: WithdrawalReasonCode) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setState({ status: "submitting" });

      let result: WithdrawResultLike;
      try {
        result = await withdraw(reasonCode);
      } catch {
        inFlight.current = false;
        // A thrown error is a transport failure, not a domain answer, and it fails
        // CLOSED: nothing is optimistically shown as withdrawn. Somebody who believes
        // they are off a hospital run when they are not is the worst outcome here.
        setState({ status: "refused", rejection: { kind: "validation_failed", issues: [] } });
        return;
      }

      inFlight.current = false;

      if (result.ok) {
        setState({ status: "withdrawn" });
        onPlanChanged?.();
        return;
      }

      setState({ status: "refused", rejection: result.rejection });
    },
    [withdraw, onPlanChanged],
  );

  const reset = useCallback(() => {
    inFlight.current = false;
    setState({ status: "idle" });
  }, []);

  return { state, submit, reset };
}
